import React, { useEffect, useState } from "react";
import { api } from "../lib/api";

/**
 * GOV-030 Import Replacement prompt · two-stage typed-confirm.
 *
 * Renders when the server returns HTTP 409 with
 * `code: "IMPORT_REPLACEMENT_DECISION_REQUIRED"`. The controller chooses:
 *   - Replace Existing Population (recommended)  → two-stage confirm
 *   - Append as Delta Import
 *   - Cancel
 *
 * Stage 1 (Replace path) shows a quantified impact summary powered by
 *   `GET /api/imports/batches/{id}/supersede-impact`  so the Controller
 *   sees exactly what will happen before committing. If the impact
 *   contains soft blockers (matched transactions), Stage 2 shifts to a
 *   final typed-"REPLACE" confirmation and the submission sets
 *   `supersede_mode = "CASCADE_UNMATCH"` on the import request.
 *
 * Append and Cancel paths bypass the two-stage flow (Append is additive,
 * Cancel is a no-op).
 */
export function ImportReplacementPrompt({ payload, onDecision, onCancel }) {
  const [decision, setDecision] = useState("replace_existing");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [stage, setStage] = useState("choose");   // 'choose' | 'confirm'
  const [impact, setImpact] = useState(null);
  const [impactLoading, setImpactLoading] = useState(false);
  const [impactError, setImpactError] = useState(null);
  const [confirmToken, setConfirmToken] = useState("");

  const active = payload?.active_batches?.[0];
  const canProceedStage1 = reason.trim().length > 0 && !submitting;

  // Pull preflight impact once we know which active batch we're superseding.
  useEffect(() => {
    if (!payload || decision !== "replace_existing" || !active?.id) return;
    setImpactLoading(true); setImpactError(null);
    api.getSupersedeImpact(active.id)
      .then(setImpact)
      .catch(e => setImpactError(e?.response?.data?.detail || e.message))
      .finally(() => setImpactLoading(false));
  }, [payload, decision, active?.id]);

  if (!payload) return null;

  const proceedToConfirm = (e) => {
    e?.preventDefault?.();
    if (!canProceedStage1) return;
    if (decision === "append_as_delta") {
      // Append doesn't need a two-stage confirm.
      return submit();
    }
    setStage("confirm");
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const opts = {
        replacement_decision: decision,
        replacement_reason: reason.trim(),
        superseded_batch_id: active?.id,
      };
      if (decision === "replace_existing"
          && impact && impact.matched_transactions > 0) {
        // Controller has typed REPLACE at Stage 2 — authorise the cascade.
        opts.supersede_mode = "CASCADE_UNMATCH";
      } else {
        opts.supersede_mode = "SAFE";
      }
      await onDecision(opts);
    } finally {
      setSubmitting(false);
    }
  };

  const shellStyle = {
    background: "#fff", maxWidth: 620, width: "92%",
    borderRadius: 8, padding: "22px 24px",
    boxShadow: "0 12px 36px rgba(0,0,0,0.18)",
    fontFamily: "system-ui, -apple-system, sans-serif", color: "#1f2937",
  };
  const overlay = {
    position: "fixed", inset: 0, background: "rgba(15,20,30,0.55)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 9999,
  };

  // ─── Stage 2 · typed REPLACE confirmation ────────────────────────
  if (stage === "confirm") {
    const willCascade = !!impact && impact.matched_transactions > 0;
    const tokenOK = confirmToken.trim() === "REPLACE";
    return (
      <div data-testid="import-replacement-prompt" style={overlay}>
        <form onSubmit={(e) => { e.preventDefault(); if (tokenOK) submit(); }}
              style={shellStyle}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>
            Cascade Unmatch Confirmation
          </h3>
          <p style={{ marginTop: 6, marginBottom: 14, fontSize: 13, color: "#4b5563" }}>
            This operation will:
          </p>

          <ul data-testid="import-replacement-confirm-effects" style={{
            listStyle: "none", padding: 0, margin: "0 0 12px 0",
            fontSize: 13, color: "#1f2937",
          }}>
            {willCascade ? (
              <>
                <li>✓ Unmatch <b>{impact.affected_match_groups}</b> MatchGroup{impact.affected_match_groups === 1 ? "" : "s"}</li>
                <li>✓ Free <b>{impact.affected_bank_transactions}</b> bank transaction{impact.affected_bank_transactions === 1 ? "" : "s"} + <b>{impact.affected_gl_transactions}</b> GL transaction{impact.affected_gl_transactions === 1 ? "" : "s"}</li>
                <li>✓ Preserve every transaction on both sides (raw + working rows only cleared for the superseded batch)</li>
                <li>✓ Write <b>{impact.affected_match_groups}</b> individual unmatch audit records</li>
              </>
            ) : (
              <li>✓ Supersede batch <b>{impact?.file_name}</b> ({impact?.row_count} rows)</li>
            )}
            <li>✓ Write one consolidated <code>supersede_orchestrated</code> audit event</li>
          </ul>

          <p data-testid="import-replacement-warning" style={{
            fontSize: 12.5, color: "#8a4a00", background: "#fff8ec",
            border: "1px solid #f0d78a", borderRadius: 6,
            padding: "8px 10px", marginBottom: 12,
          }}>
            This cannot be automatically reversed. The audit trail lets you
            reconstruct which pairs were unmatched, but each match must be
            re-created (Recognition Engine or manual) if you want it back.
          </p>

          <div style={{ marginBottom: 14 }}>
            <label htmlFor="gov030-confirm-token"
                   style={{ display: "block", fontSize: 12.5, fontWeight: 600, marginBottom: 4 }}>
              Type <code style={{ background: "#f3f4f6", padding: "1px 6px", borderRadius: 3 }}>REPLACE</code> to continue
            </label>
            <input id="gov030-confirm-token"
                   data-testid="import-replacement-confirm-token"
                   value={confirmToken}
                   onChange={(e) => setConfirmToken(e.target.value)}
                   autoFocus
                   placeholder="REPLACE"
                   style={{
                     width: "100%", boxSizing: "border-box",
                     fontSize: 15, fontFamily: "monospace",
                     letterSpacing: 1.5, textTransform: "uppercase",
                     padding: "10px 12px", border: "1.5px solid " + (tokenOK ? "#0b7d3a" : "#d1d5db"),
                     borderRadius: 6,
                   }} />
          </div>

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button type="button"
                    data-testid="import-replacement-back"
                    onClick={() => { setStage("choose"); setConfirmToken(""); }}
                    style={{
                      padding: "7px 14px", borderRadius: 6, border: "1px solid #d1d5db",
                      background: "#fff", color: "#374151", fontSize: 13, cursor: "pointer",
                    }}>
              Back
            </button>
            <button type="submit"
                    data-testid="import-replacement-final-submit"
                    disabled={!tokenOK || submitting}
                    style={{
                      padding: "7px 16px", borderRadius: 6, border: "none",
                      background: tokenOK && !submitting ? "#b1361b" : "#9ca3af",
                      color: "#fff", fontSize: 13, fontWeight: 600,
                      cursor: (tokenOK && !submitting) ? "pointer" : "not-allowed",
                    }}>
              {submitting ? "Working…" : "Replace Import"}
            </button>
          </div>
        </form>
      </div>
    );
  }

  // ─── Stage 1 · Choose Replace/Append + reason + quantified impact ─
  return (
    <div data-testid="import-replacement-prompt" style={overlay}>
      <form onSubmit={proceedToConfirm} style={shellStyle}>
        <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>
          Import already exists for this period
        </h3>
        <p style={{ marginTop: 8, marginBottom: 14, fontSize: 13, color: "#4b5563" }}>
          {payload.prompt_message}
        </p>

        {active && (
          <div data-testid="import-replacement-active-batch"
               style={{
                 background: "#f6f7f9", border: "1px solid #e3e6eb",
                 borderRadius: 6, padding: "10px 12px", fontSize: 12.5,
                 color: "#374151", marginBottom: 14, lineHeight: 1.5,
               }}>
            <div><b>Existing batch:</b> {active.file_name}</div>
            <div><b>Rows:</b> {active.source_row_count}</div>
            <div>
              <b>DR / CR:</b> ${Number(active.source_total_debits || 0).toLocaleString()} /
              ${Number(active.source_total_credits || 0).toLocaleString()}
            </div>
            <div style={{ color: "#6b7280" }}>id: {active.id}</div>
          </div>
        )}

        <div style={{ marginBottom: 12 }}>
          <label style={{
            display: "flex", alignItems: "flex-start", gap: 8,
            padding: "8px 10px", borderRadius: 6,
            border: `1.5px solid ${decision === "replace_existing" ? "#1e5d34" : "#d1d5db"}`,
            background: decision === "replace_existing" ? "#f1f8f3" : "transparent",
            cursor: "pointer", marginBottom: 8,
          }}>
            <input type="radio" name="gov030-decision" value="replace_existing"
                   checked={decision === "replace_existing"}
                   onChange={(e) => setDecision(e.target.value)}
                   data-testid="import-replacement-radio-replace"
                   style={{ marginTop: 3 }} />
            <div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>
                Replace Existing Population <span style={{ color: "#1e5d34", fontWeight: 700 }}>(Recommended)</span>
              </div>
              <div style={{ fontSize: 12, color: "#6b7280" }}>
                Supersede the prior batch. Its raw + working rows are cascade-removed;
                the batch record is preserved for audit lineage.
              </div>
            </div>
          </label>

          <label style={{
            display: "flex", alignItems: "flex-start", gap: 8,
            padding: "8px 10px", borderRadius: 6,
            border: `1.5px solid ${decision === "append_as_delta" ? "#1f4d8a" : "#d1d5db"}`,
            background: decision === "append_as_delta" ? "#eef3fa" : "transparent",
            cursor: "pointer",
          }}>
            <input type="radio" name="gov030-decision" value="append_as_delta"
                   checked={decision === "append_as_delta"}
                   onChange={(e) => setDecision(e.target.value)}
                   data-testid="import-replacement-radio-append"
                   style={{ marginTop: 3 }} />
            <div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>Append as Delta Import</div>
              <div style={{ fontSize: 12, color: "#6b7280" }}>
                Add this file on top of the active batch. Use only for explicit period-end
                deltas. The system returns fingerprint-collision warnings if any rows
                duplicate the parent batch.
              </div>
            </div>
          </label>
        </div>

        {/* Quantified impact panel — only when Replace is selected. */}
        {decision === "replace_existing" && (
          <div data-testid="import-replacement-impact"
               style={{
                 background: "#fff8ec", border: "1px solid #f0d78a",
                 borderRadius: 6, padding: "10px 12px", fontSize: 12.5,
                 color: "#5e4000", marginBottom: 14,
               }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Impact</div>
            {impactLoading && <div className="muted">Assessing impact…</div>}
            {impactError && <div style={{ color: "#8b1c1c" }}>Impact assessment failed: {impactError}</div>}
            {impact && !impactLoading && (
              <ul style={{ margin: "4px 0 0 16px", padding: 0, lineHeight: 1.5 }}>
                {impact.matched_transactions > 0 ? (
                  <>
                    <li>
                      <b>{impact.affected_match_groups}</b> MatchGroup{impact.affected_match_groups === 1 ? "" : "s"} will be unmatched
                    </li>
                    <li>
                      <b>{impact.affected_bank_transactions}</b> bank transaction{impact.affected_bank_transactions === 1 ? "" : "s"} will become unmatched
                    </li>
                    <li>
                      <b>{impact.affected_gl_transactions}</b> GL transaction{impact.affected_gl_transactions === 1 ? "" : "s"} will become unmatched
                    </li>
                    <li>No certifications, journals, or locked periods will be affected.</li>
                  </>
                ) : (
                  <li>No matched transactions. Batch will be superseded cleanly.</li>
                )}
                {impact.hard_blockers?.length > 0 && (
                  <li style={{ color: "#8b1c1c", marginTop: 4 }}>
                    <b>Cannot proceed:</b> {impact.hard_blockers[0].detail}
                  </li>
                )}
              </ul>
            )}
          </div>
        )}

        <div style={{ marginBottom: 14 }}>
          <label htmlFor="gov030-reason"
                 style={{ display: "block", fontSize: 12.5, fontWeight: 600, marginBottom: 4 }}>
            Reason (required for audit)
          </label>
          <textarea id="gov030-reason"
                    data-testid="import-replacement-reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={3}
                    placeholder="e.g. 'Refreshed GL export from ERP; v2 contains period-end batch'."
                    style={{
                      width: "100%", boxSizing: "border-box",
                      fontSize: 13, fontFamily: "inherit",
                      padding: "8px 10px", border: "1px solid #d1d5db",
                      borderRadius: 6, resize: "vertical",
                    }} />
        </div>

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button type="button"
                  data-testid="import-replacement-cancel"
                  onClick={onCancel}
                  style={{
                    padding: "7px 14px", borderRadius: 6, border: "1px solid #d1d5db",
                    background: "#fff", color: "#374151", fontSize: 13, cursor: "pointer",
                  }}>
            Cancel Import
          </button>
          <button type="submit"
                  data-testid="import-replacement-confirm"
                  disabled={!canProceedStage1
                            || (decision === "replace_existing"
                                && (impact?.hard_blockers?.length > 0 || impactLoading))}
                  style={{
                    padding: "7px 16px", borderRadius: 6, border: "none",
                    background: (canProceedStage1
                                 && !(decision === "replace_existing"
                                      && (impact?.hard_blockers?.length > 0 || impactLoading)))
                                ? "#1e5d34" : "#9ca3af",
                    color: "#fff", fontSize: 13, fontWeight: 600,
                    cursor: canProceedStage1 ? "pointer" : "not-allowed",
                  }}>
            {submitting
              ? "Working…"
              : decision === "replace_existing" ? "Continue →" : "Append Delta"}
          </button>
        </div>
      </form>
    </div>
  );
}
